import { useSession } from "next-auth/react";

const routes = {
    reviews: '/reviews',
    actions: '/actions',
    projects: '/projects',
    demands: '/demands',
    allocations: '/allocations',
    requisitions: '/requisitions',
    reports: '/reports',
    reporteeList: '/reportee-list/reportee',
    availabilityReport: '/availability-report',
    profile: '/',
    stockCount: '/stock-count',
    firmInitiatives: '/firm-initiatives',
    workpackages: '/work-packages',
    pods: '/pods',
    assignedPods: '/assigned-pods',
    podWorkPackages: '/work-packages-pod-lead',
    propelReport: '/propel-report'
};

const delegatePathsForRoles = {
    partner: {
        all: [routes.reviews],
        requisition: [routes.reviews],
        project: [routes.reviews, routes.availabilityReport]
    },
    manager: {
        all: [routes.actions],
        project: [routes.actions, routes.availabilityReport]
    }
};
const delegatePathsForTypes = {
    all: [routes.projects, routes.demands, routes.allocations, routes.requisitions, routes.reports, routes.reporteeList,
    routes.availabilityReport, routes.profile, routes.stockCount, routes.propelReport, routes.workpackages, routes.pods, routes.assignedPods, routes.podWorkPackages],
    project: [routes.projects, routes.demands, routes.allocations, routes.requisitions, routes.propelReport],
    requisition: [routes.requisitions],
    profile: [routes.profile],
    stockCount: [routes.stockCount],
    specificProject: [routes.projects, routes.demands, routes.allocations, routes.requisitions, routes.propelReport],
    specificRequisition: [routes.requisitions],
    firmInitiative: [routes.firmInitiatives],
    specificFirmInitiative: [routes.firmInitiatives],
    workPackage: [routes.workpackages, routes.pods],
    specificWorkPackage: [routes.workpackages, routes.pods],
    pod: [routes.assignedPods, routes.podWorkPackages],
    specificPod: [routes.assignedPods, routes.podWorkPackages]
};

const getPathsForDelegate = ({ delegateRoles = [], delegateTypes = [] }) => {
    const paths = new Set();

    const addPaths = (pathArray) => pathArray?.forEach((path) => paths.add(path));

    delegateRoles.forEach((role) => {
        delegateTypes.forEach((type) => {
            addPaths(delegatePathsForRoles[role]?.[type]);
        });
    });

    delegateTypes.forEach((type) => {
        addPaths(delegatePathsForTypes[type]);
    });

    return Array.from(paths);
};

const ACCESS_TYPE_CONFIG_FOR_GUEST_RM = {
    all: {
        allowAll: true,
    },
    dashboard: {
        defaultRedirect: '/',
        allowedPaths: ['/', '/projects', '/demands', '/allocations', '/requisitions', '/actions', '/employees/'],
    },
    projects: {
        defaultRedirect: '/projects',
        allowedPaths: ['/projects', '/demands', '/allocations', '/requisitions', '/employees/'],
    },
    requisitions: {
        defaultRedirect: '/requisitions',
        allowedPaths: ['/requisitions', '/employees/'],
    },
    employees: {
        defaultRedirect: '/employees',
        allowedPaths: ['/employees', '/reportee-list/reportee'],
    },
    reports: {
        defaultRedirect: '/availability-report',
        allowedPaths: ['/availability-report', '/export-report', '/status-report', '/download-history', '/stock-cash-count-availability-report', '/employees/'],
    },
    review_tab: {
        defaultRedirect: '/actions?gradeLevel=rm-review',
        allowedPaths: ['/actions?gradeLevel=rm-review', '/requisitions/', '/employees/'],
    },
};


const ROLE_BASED_PATHS = {
    partner: ['/reviews'],
    manager: ['/actions?gradeLevel=manager-review'],
};

const ALWAYS_ALLOWED_PATHS = ['/login', '/logout', '/temp-login', '/not-authorized', '/resource-dashboard', '/my-profile', '/my-schedules', '/timesheet', '/job-posting'];

const MENU_ITEMS = () => {
    return {
        dashboard: ['Home', 'Projects', 'Demands', 'Allocations', 'Requisitions', 'RM Review', 'Reviews'],
        projects: ['Projects', 'Demands', 'Allocations', 'Requisitions'],
        requisitions: ['Requisitions'],
        employees: ['Employees', 'Reportees'],
        reports: ['Reports'],
        review_tab: ['Reviews']
    }
}

const useEmployeeAccess = () => {
    const { data: session } = useSession();
    const role = session?.user?.role;
    const gradeLevel = session?.user?.gradeLevel;
    const isGuestRm = role.includes('guest_rm');
    const accessType = session?.user?.accessType;

    if (
        role.some((r) => ['admin', 'resource_manager', 'resource_manager_lead', 'talent_advisor', 'guest_rm', 'pod_lead', 'service_category_lead', 'service_line_lead', 'tpcr_rm', 'tpcr_rm_lead'].includes(r))
        || ['manager', 'partner'].includes(gradeLevel)
    ) {
        return true;
    }
    return false;
}

const useRequisitionAccess = () => {
    const { data: session } = useSession();
    const role = session?.user?.role;
    const isGuestRm = role.includes('guest_rm');
    const accessType = session?.user?.accessType;

    if (role.includes('admin') || role.includes('resource_manager') || role.includes('resource_manager_lead')) {
        return true;
    }
    return isGuestRm && ['all', 'dashboard', 'projects', 'requisitions'].includes(accessType);
}

const businessTeamPathForResource = (pathName, session) => {
    const businessTeamPathForResource = ['/', '/projects', '/demands', '/allocations', '/requisitions', '/availability-report', '/download-history']
    const isBusinessTeam = session?.user?.role?.includes('business_team');
    const isAudit = session?.user?.function === 'Audit & Assurance' && session?.user?.serviceArea === 'Audit';
    const isAuditRM = session?.user?.isAuditRM;
    const ALWAYS_ALLOWED_PATHS_LOCAL = ['/not-authorized', '/login', '/logout', '/temp-login', '/resource-dashboard', '/my-profile', '/my-schedules', '/timesheet', '/job-posting'];
    if (isBusinessTeam && (isAudit || isAuditRM)) {
        businessTeamPathForResource.push('/stock-count');
        businessTeamPathForResource.push('/stock-cash-count-availability-report');
    }
    const isAllowedPath = businessTeamPathForResource.some(allowedPath => {
        return pathName === allowedPath || (pathName.startsWith(allowedPath) && allowedPath !== '/');
    });

    const isAlwaysAllowed = ALWAYS_ALLOWED_PATHS_LOCAL.some(allowedPath => {
        return pathName === allowedPath || (pathName.startsWith(allowedPath) && allowedPath !== '/');
    });

    return (isAllowedPath || isAlwaysAllowed);
};

const GLOBAL_ALLOWED_PATHS = ['/login', '/logout', '/temp-login', '/not-authorized', '/download-history'];

export { ACCESS_TYPE_CONFIG_FOR_GUEST_RM, ALWAYS_ALLOWED_PATHS, businessTeamPathForResource, delegatePathsForRoles, delegatePathsForTypes, getPathsForDelegate, GLOBAL_ALLOWED_PATHS, MENU_ITEMS, ROLE_BASED_PATHS, routes, useEmployeeAccess, useRequisitionAccess };

